import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import AdminPage from '../../../components/admin/AdminPage';
import Users from './Users';
import ReportingAndOrgChart from './ReportingAndOrgChart';
import Permission from './permission';
import Trainees from '../trainees/trainees';
import { usePermissions } from '../../../context/PermissionsContext';
import { toast } from 'react-toastify';

function RolesAndAccess() {
  const { permissions } = usePermissions();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState('trainees');

  const tabs = [
    ...(permissions.trainee_management_view
      ? [
          { id: 'trainees', label: 'Trainees', icon: 'fluent:people-20-regular', component: Trainees, componentProps: { forceTab: 'trainees' } },
          { id: 'groups', label: 'Trainee Groups', icon: 'fluent:people-team-20-regular', component: Trainees, componentProps: { forceTab: 'groups' } }
        ]
      : []),
    permissions.roles_and_access_user_view && {
      id: 'users',
      label: 'L&D Team',
      icon: 'fluent:people-24-regular',
      component: Users
    },
    permissions.roles_and_access_user_view && {
      id: 'reporting',
      label: 'Reporting',
      icon: 'fluent:organization-24-regular',
      component: ReportingAndOrgChart
    },
    {
      id: 'permissions',
      label: 'Roles & Permissions',
      icon: 'fluent:shield-lock-24-regular',
      component: Permission,
      disabled: !permissions.roles_and_access_permissions_view
    }
  ].filter(Boolean);

  const tabIds = tabs.map((t) => t.id);

  useEffect(() => {
    const fromState = location.state?.accessTab;
    if (fromState && tabIds.includes(fromState)) {
      setActiveTab(fromState);
    } else if (tabParam && tabIds.includes(tabParam)) {
      setActiveTab(tabParam);
    } else if (tabIds.length > 0 && !tabIds.includes(activeTab)) {
      setActiveTab(tabIds[0]);
    }
  }, [location.state?.accessTab, tabParam, permissions.trainee_management_view, permissions.roles_and_access_user_view]);

  const setActiveTabAndUrl = (tabId) => {
    if (tabId === 'permissions' && !permissions.roles_and_access_permissions_view) {
      toast.warning("You don't have permission to view roles and permissions");
      return;
    }
    setActiveTab(tabId);
    setSearchParams(tabId === 'trainees' ? {} : { tab: tabId });
  };

  const effectiveTab = tabIds.includes(activeTab) ? activeTab : tabIds[0] || 'trainees';

  return (
    <AdminPage noPadding>
      <div className="w-full px-6 pt-0 pb-4 animate-in fade-in duration-700">
        
        {/* Modern Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-5 pb-5 border-b border-gray-100 gap-6">
            <div className="space-y-1">
                <div className="flex items-center gap-2 text-[#7083FF] font-bold text-xs uppercase tracking-[0.2em] mb-2">
                    <Icon icon="fluent:shield-keyhole-24-regular" width={16} />
                    <span>Security & Access</span>
                </div>
                <h1 className="text-4xl font-black text-gray-900 tracking-tight">
                  Management <span className="font-black text-gray-400">Access</span>
                </h1>
            </div>

            <div className="flex items-center gap-3">
              {/* Global Refresh Action */}
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="p-3 rounded-2xl bg-white border border-gray-100 text-gray-400 hover:text-[#7083FF] hover:bg-[#7083FF]/5 transition-all shadow-sm group/refresh"
                title="Refresh Page"
              >
                <Icon icon="fluent:arrow-sync-24-regular" width={20} className="group-active/refresh:rotate-180 transition-transform duration-500" />
              </button>

              {/* Tab Navigation Hub - More Premium */}
              <div className="p-1.5 bg-gray-50 rounded-[28px] border border-gray-100 flex flex-nowrap gap-1 items-center shadow-sm overflow-x-auto">
                {tabs.map((tab) => {
                  const isActive = effectiveTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTabAndUrl(tab.id)}
                      disabled={tab.disabled}
                      className={`relative flex items-center gap-2.5 px-6 py-3.5 rounded-[22px] text-sm font-bold transition-all duration-500 group whitespace-nowrap ${
                        isActive
                          ? 'bg-white text-gray-900 shadow-xl shadow-gray-200/50 ring-1 ring-black/[0.02]'
                          : 'text-gray-400 hover:text-gray-600 hover:bg-white/50'
                      } ${tab.disabled ? 'cursor-not-allowed opacity-40' : ''}`}
                    >
                      <Icon
                        icon={tab.icon}
                        width={20}
                        height={20}
                        className={isActive ? 'text-[#7083FF]' : 'text-gray-400 group-hover:text-gray-600'}
                      />
                      <span>{tab.label}</span>
                      {isActive && (
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-[#7083FF] rounded-full" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
        </div>


        {/* Tab content with smooth transition */}
        <div className="min-h-[600px] relative">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`${effectiveTab === tab.id ? 'block opacity-100 translate-y-0' : 'hidden opacity-0 translate-y-4'} transition-all duration-700 ease-out`}
            >
              {(!tab.disabled || tab.id === 'users') && (
                <div className="animate-in fade-in slide-in-from-bottom-3 duration-500 ease-out fill-mode-forwards">
                  <tab.component {...(tab.componentProps || {})} />
                </div>
              )}
            </div>
          ))}
        </div>

      <style>{`
        @keyframes rolesTabFade {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .admin-page { padding: 0 !important; }
      `}</style>
      </div>
    </AdminPage>
  );
}

export default RolesAndAccess;
